import React from 'react';
import { Helmet } from 'react-helmet';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Calendar, Star, Gift, CheckCircle, Target, Zap } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const EmployeeRewardsPlatforms = () => {
  const benefits = [
    {
      icon: <Target className="h-6 w-6 text-primary" />,
      title: "Drive Performance",
      description: "Reward platforms can increase performance by up to 25% through targeted incentives"
    },
    {
      icon: <Zap className="h-6 w-6 text-primary" />,
      title: "Boost Motivation",
      description: "Tangible rewards create lasting motivation and encourage continued excellence"
    },
    {
      icon: <Gift className="h-6 w-6 text-primary" />,
      title: "Flexible Options",
      description: "Offer diverse reward catalogs that cater to different preferences and demographics"
    }
  ];

  const vendors = [
    {
      name: "Assembly",
      rating: 4.9,
      description: "Comprehensive rewards platform with customizable catalogs, points system, and seamless integration.",
      features: ["Custom Rewards Catalog", "Points Management", "Budget Controls", "Global Fulfillment"],
      badge: "Editor's Choice"
    },
    {
      name: "Tremendous",
      rating: 4.7,
      description: "Flexible rewards API and platform for digital gift cards, prepaid cards, and cash rewards.",
      features: ["Gift Card Rewards", "API Integration", "Global Coverage", "Instant Delivery"],
      badge: "Best API"
    },
    {
      name: "Tango Card",
      rating: 4.6,
      description: "Digital rewards platform specializing in gift cards and incentive programs for businesses.",
      features: ["Digital Gift Cards", "Brand Partnerships", "Bulk Orders", "Real-time Reporting"],
      badge: "Best Selection"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Best Employee Rewards Platforms | Updated Annually</title>
        <meta name="description" content="Compare the best employee rewards platforms to drive engagement and motivate your team. Updated annually with comprehensive vendor reviews and rankings." />
        <meta name="keywords" content="best employee rewards platforms, employee rewards software, rewards platform comparison, top rewards tools" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "Best Employee Rewards Platforms",
            "description": "Top-rated employee rewards platforms",
            "itemListElement": vendors.map((vendor, index) => ({
              "@type": "SoftwareApplication",
              "position": index + 1,
              "name": vendor.name,
              "description": vendor.description,
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": vendor.rating,
                "bestRating": 5
              }
            }))
          })}
        </script>
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <Navbar />
        
        <main className="pt-24 pb-16">
          {/* Hero Section */}
          <section className="px-6 max-w-7xl mx-auto mb-16">
            <div className="mb-6">
              <nav className="text-sm text-muted-foreground mb-4">
                <a href="/best" className="hover:text-primary">Best Software</a> / Employee Rewards Platforms
              </nav>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Best Employee Rewards Platforms
              </h1>
              <p className="text-xl text-muted-foreground max-w-4xl leading-relaxed mb-8">
                Employee rewards platforms help organizations motivate teams through meaningful incentives and recognition. 
                These solutions offer flexible reward catalogs, points systems, and seamless delivery to drive engagement and performance.
              </p>
              
              <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full text-primary font-medium">
                <Calendar className="h-4 w-4" />
                Last Updated: January 2025
              </div>
            </div>
          </section>

          {/* Why Rewards Platforms Matter */}
          <section className="px-6 max-w-7xl mx-auto mb-16">
            <h2 className="text-3xl font-bold mb-8">Why Employee Rewards Platforms Matter</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <Card key={index} className="border-l-4 border-l-primary">
                  <CardHeader className="pb-4">
                    <div className="flex items-center gap-3 mb-2">
                      {benefit.icon}
                      <CardTitle className="text-lg">{benefit.title}</CardTitle>
                    </div>
                    <CardDescription className="text-base">
                      {benefit.description}
                    </CardDescription>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </section>

          {/* Annual Rankings */}
          <section className="px-6 max-w-7xl mx-auto mb-16">
            <h2 className="text-3xl font-bold mb-8">Annual Rankings</h2>
            <div className="bg-muted/30 rounded-xl p-8">
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-primary" />
                      2025 Rankings
                    </CardTitle>
                    <CardDescription>
                      Our comprehensive 2025 analysis of the best employee rewards platforms.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button className="w-full" disabled>
                      Coming Soon
                    </Button>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-muted-foreground" />
                      2026 Rankings
                    </CardTitle>
                    <CardDescription>
                      Stay tuned for our 2026 rankings with the latest market developments.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button variant="outline" className="w-full" disabled>
                      Coming Soon
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>

          {/* Vendor Spotlights */}
          <section className="px-6 max-w-7xl mx-auto mb-16">
            <h2 className="text-3xl font-bold mb-8">Top Employee Rewards Platforms</h2>
            <div className="space-y-6">
              {vendors.map((vendor, index) => (
                <Card key={index} className="group hover:shadow-lg transition-all duration-300">
                  <CardHeader>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center font-bold text-2xl">
                          {vendor.name.charAt(0)}
                        </div>
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <CardTitle className="text-2xl">{vendor.name}</CardTitle>
                            {vendor.badge && (
                              <Badge variant="secondary" className="bg-primary/10 text-primary">
                                {vendor.badge}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1">
                              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                              <span className="font-semibold">{vendor.rating}</span>
                            </div>
                            <span className="text-muted-foreground">out of 5</span>
                          </div>
                        </div>
                      </div>
                      <Button variant="outline" className="group-hover:bg-primary group-hover:text-primary-foreground">
                        View Details
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">{vendor.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {vendor.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center gap-1 text-sm bg-muted px-3 py-1 rounded-full">
                          <CheckCircle className="h-3 w-3 text-primary" />
                          {feature}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section className="px-6 max-w-7xl mx-auto">
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="p-8 text-center">
                <h2 className="text-2xl font-bold mb-4">Ready to Launch Your Rewards Program?</h2>
                <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                  Compare these leading platforms and find the perfect rewards solution for your team.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg">
                    Book a Demo
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                  <Button variant="outline" size="lg" asChild>
                    <a href="/best">View All Categories</a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default EmployeeRewardsPlatforms;